import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { FilestackImageData } from 'api/types/shared.types';
import { Box } from 'components/Box';
import { NoAmp } from 'containers/NoAmp';
import { getImageSrc } from 'utils/getImageSrc';

import { Image } from './Image';
import { ImageSpinner } from './ImageSpinner';

const imageFadeIn = (fadeInBlurAmount: number) => keyframes`
  0% {
    filter: blur(${fadeInBlurAmount}px);
    transform: scale(1.05);
    opacity: 0;
  }

  33% {
    filter: blur(${fadeInBlurAmount}px);
    transform: scale(1.05);
    opacity: 0.25;
  }

  100% {
    filter: blur(0);
    opacity: 1;
  }
`;

const AmpImageWrap = styled.div<{
  ampHeight: number | string;
  ampLeftPosition: number | string;
  ampTopPosition: number | string;
}>`
  position: relative;
  width: 100%;
  ${({ ampHeight }) => `height: ${ampHeight}px;`}

  /* stylelint-disable */
  amp-img img {
    object-fit: contain;
    ${({ ampLeftPosition, ampTopPosition }) => `
      object-position: ${ampLeftPosition} ${ampTopPosition};
    `}
  }
`;

type LazyBoxProps = {
  $preloaded: boolean;
  $loaded: boolean;
  $blurAmount: number;
};

const LazyBox = styled(Box)<LazyBoxProps>`
  max-width: 100%;
  position: relative;
  ${(props) =>
    !props.$preloaded &&
    props.$loaded &&
    css`
      img {
        animation: ${imageFadeIn(props.$blurAmount)} 1s forwards;
      }
    `}
  ${(props) =>
    !props.$preloaded &&
    !props.$loaded &&
    css`
      img {
        filter: blur(20px);
        transform: scale(1.05);
        opacity: 0;
      }
    `}
    img {
    display: block;
  }
`;

export function FilestackImage({
  alt,
  centered,
  data,
  height: heightProp,
  width: widthProp,
  round,
  fit,
  'data-qa-id': qaId,
  layout,
  format,
  quality,
  ampHeight,
  ampLeftPosition,
  ampTopPosition,
  className,
}: {
  alt: string;
  centered?: boolean;
  className?: string;
  data: FilestackImageData | null | undefined;
  height?: number;
  width?: number;
  round?: boolean;
  fit?: string;
  layout?: string;
  format?: string;
  'data-qa-id'?: string;
  quality?: number;
  ampHeight?: number;
  ampLeftPosition?: string;
  ampTopPosition?: string;
}) {
  const hasDimensionProp = Boolean(widthProp || heightProp);
  const aspectRatio =
    data && data.width && data.height ? data.width / data.height : null;

  let width;
  let height;

  if (hasDimensionProp) {
    width =
      widthProp ||
      (aspectRatio && heightProp && Math.round(heightProp * aspectRatio)) ||
      undefined;
    height =
      heightProp ||
      (aspectRatio && widthProp && Math.round(widthProp / aspectRatio)) ||
      undefined;
  } else {
    width = data && data.width ? data.width : undefined;
    height = data && data.height ? data.height : undefined;
  }

  const handle = data ? data.handle : '';
  const mimetype = data ? data.mimetype : '';
  const src = getImageSrc({
    handle,
    width,
    height,
    round,
    fit,
    format: mimetype === 'image/tiff' ? 'jpg' : format,
    quality,
    mimetype,
  });
  const srcSet = `${getImageSrc({
    handle,
    width,
    height,
    round,
    fit,
    format: mimetype === 'image/tiff' ? 'jpg' : format,
    quality,
    mimetype,
  })},
    ${getImageSrc({
      handle,
      width: width ? width * 2 : undefined,
      height: height ? height * 2 : undefined,
      round,
      fit,
      format: mimetype === 'image/tiff' ? 'jpg' : format,
      quality,
      mimetype,
    })} 2x`;
  const [loaded, setLoaded] = useState(false);
  const [preloaded, setPreloaded] = useState(true);
  const [dimensions, setDimensions] = useState({ width, height });
  const imgEl = useRef(null);

  const onLoad = () => {
    setDimensions({
      width: imgEl.current ? imgEl.current.offsetWidth : 0,
      height: imgEl.current ? imgEl.current.offsetHeight : 0,
    });
    setLoaded(true);
  };

  useEffect(() => {
    if (imgEl && imgEl.current && imgEl.current.complete) {
      setPreloaded(true);
      setLoaded(true);
    } else {
      setPreloaded(false);
      setLoaded(false);
    }
  }, [fit, handle, height, round, width]);

  if (!data) {
    return null;
  }

  const blurAmount = (() => {
    if (width) return width / 50;
    if (height) return height / 50;

    return 20;
  })();

  return (
    <Box
      position="relative"
      overflow="hidden"
      borderRadius={round ? '50%' : undefined}
      className={className}
    >
      <NoAmp>
        <ImageSpinner
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          opacity={loaded ? 0 : 1}
        />
      </NoAmp>
      <LazyBox
        $loaded={loaded}
        $preloaded={preloaded}
        position="relative"
        margin={centered ? '0 auto' : undefined}
        width={dimensions.width}
        $blurAmount={blurAmount}
      >
        <Box position="relative">
          {round ? (
            <Image
              ref={imgEl}
              onLoad={onLoad}
              data-qa-id={qaId}
              height={height}
              width={width}
              src={src}
              srcSet={srcSet}
              alt={alt}
              layout={layout}
              quality={quality}
            />
          ) : (
            <>
              {!loaded && (
                <Box opacity={0}>
                  <Image
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={getImageSrc({
                      handle,
                      width: width ? Math.ceil(width / 30) : undefined,
                      height: height ? Math.ceil(height / 30) : undefined,
                      round,
                      fit,
                      mimetype,
                    })}
                    alt={alt}
                    layout={layout}
                    alwaysShow
                  />
                </Box>
              )}
              {ampHeight ? (
                <AmpImageWrap
                  ampHeight={ampHeight}
                  ampLeftPosition={ampLeftPosition}
                  ampTopPosition={ampTopPosition}
                >
                  <Image
                    ref={imgEl}
                    onLoad={onLoad}
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={src}
                    srcSet={srcSet}
                    alt={alt}
                    layout={layout}
                    quality={quality}
                  />
                </AmpImageWrap>
              ) : (
                <Box
                  position={loaded ? undefined : 'absolute'}
                  width={loaded ? undefined : '100%'}
                  height={loaded ? undefined : '100%'}
                  top={0}
                  left={0}
                >
                  <Image
                    ref={imgEl}
                    onLoad={onLoad}
                    data-qa-id={qaId}
                    height={height}
                    width={width}
                    src={src}
                    srcSet={srcSet}
                    alt={alt}
                    layout={layout}
                    quality={quality}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </LazyBox>
    </Box>
  );
}
